import { useEffect, useMemo, useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useAttributesByCategory } from '@/hooks/use-attributes'
import { cn } from '@/lib/utils'
import type { ResourceTagRecord, ResourceTagRecordInput } from '@/types/resource-tag'

interface RecordFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'add' | 'edit'
  resourceTagName: string
  initial?: Pick<ResourceTagRecord, 'resourceAttribute' | 'resourceValue'> | null
  onSubmit: (data: ResourceTagRecordInput) => void
  isLoading?: boolean
}

export function RecordFormDialog({
  open,
  onOpenChange,
  mode,
  resourceTagName,
  initial,
  onSubmit,
  isLoading = false,
}: RecordFormDialogProps) {
  const { data: attrsData, isLoading: attrsLoading } = useAttributesByCategory('RESOURCE', {
    pageSize: 500,
  })
  const options = attrsData?.data ?? []

  const [resourceAttribute, setResourceAttribute] = useState('')
  const [resourceValue, setResourceValue] = useState('')
  const [attrOpen, setAttrOpen] = useState(false)

  useEffect(() => {
    if (open) {
      setResourceAttribute(initial?.resourceAttribute ?? '')
      setResourceValue(initial?.resourceValue ?? '')
      setAttrOpen(false)
    }
  }, [open, initial])

  const selected = useMemo(
    () => options.find((a) => a.key === resourceAttribute),
    [options, resourceAttribute]
  )

  const displayLabel = selected
    ? `${selected.displayName} (${selected.key})`
    : resourceAttribute || (attrsLoading ? 'Loading attributes…' : 'Select attribute')

  const canSubmit =
    resourceAttribute.trim().length > 0 &&
    resourceValue.trim().length > 0 &&
    !isLoading

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    onSubmit({
      resourceAttribute: resourceAttribute.trim(),
      resourceValue: resourceValue.trim(),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md overflow-visible">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{mode === 'add' ? 'Add record' : 'Edit record'}</DialogTitle>
            <DialogDescription>
              Map a resource attribute value to{' '}
              <span className="font-medium text-foreground">{resourceTagName}</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="resource-tag-readonly">Data Classification Attribute</Label>
              <Input id="resource-tag-readonly" value={resourceTagName} disabled />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="resource-attribute">
                Resource attribute <span className="text-destructive">*</span>
              </Label>
              {mode === 'edit' ? (
                <Input id="resource-attribute" value={resourceAttribute} disabled />
              ) : (
                <Popover modal open={attrOpen} onOpenChange={setAttrOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      id="resource-attribute"
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={attrOpen}
                      disabled={attrsLoading || isLoading}
                      className="h-9 w-full justify-between font-normal"
                    >
                      <span
                        className={cn(
                          'truncate text-left',
                          !resourceAttribute && 'text-muted-foreground'
                        )}
                      >
                        {displayLabel}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                    side="bottom"
                    sideOffset={4}
                    collisionPadding={8}
                  >
                    <Command>
                      <CommandInput placeholder="Search attributes…" />
                      <CommandList className="max-h-56">
                        <CommandEmpty>
                          {attrsLoading ? 'Loading…' : 'No resource attributes found.'}
                        </CommandEmpty>
                        <CommandGroup>
                          {options.map((attr) => (
                            <CommandItem
                              key={attr.id}
                              value={`${attr.key} ${attr.displayName}`}
                              onSelect={() => {
                                setResourceAttribute(attr.key)
                                setAttrOpen(false)
                              }}
                              className="gap-2"
                            >
                              <Check
                                className={cn(
                                  'h-4 w-4 shrink-0',
                                  attr.key === resourceAttribute ? 'opacity-100' : 'opacity-0'
                                )}
                              />
                              <span className="grid min-w-0 flex-1 grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] items-center gap-3">
                                <span className="truncate font-mono text-sm" title={attr.key}>
                                  {attr.key}
                                </span>
                                <span
                                  className="truncate text-sm text-muted-foreground"
                                  title={attr.displayName}
                                >
                                  {attr.displayName}
                                </span>
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="resource-value">
                Resource value <span className="text-destructive">*</span>
              </Label>
              <Input
                id="resource-value"
                value={resourceValue}
                onChange={(e) => setResourceValue(e.target.value)}
                disabled={isLoading}
                placeholder="e.g. 005"
                autoComplete="off"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit || (mode === 'add' && options.length === 0)}
            >
              {isLoading ? 'Saving…' : mode === 'add' ? 'Add' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
